/**
 * Deterministic opponent AI.
 *
 * The controller observes current/past public combat state only, waits a difficulty-dependent
 * reaction delay, then drives Player 2 through the same InputSystem snapshot path as a human.
 * It has no access to future inputs and receives no hidden collision result.
 */

import { System, clamp } from '../core/index.js';
import { PHASE } from '../combat/index.js';

export const AI_DIFFICULTIES = Object.freeze({
  rookie: Object.freeze({
    reaction: 42, jitter: 20, guardChance: 0.38, punishChance: 0.18,
    throwChance: 0.08, lowChance: 0.12, mistakeChance: 0.24, actionHold: 7,
  }),
  club: Object.freeze({
    reaction: 30, jitter: 14, guardChance: 0.5, punishChance: 0.3,
    throwChance: 0.13, lowChance: 0.16, mistakeChance: 0.16, actionHold: 6,
  }),
  skilled: Object.freeze({
    reaction: 20, jitter: 10, guardChance: 0.64, punishChance: 0.48,
    throwChance: 0.18, lowChance: 0.2, mistakeChance: 0.09, actionHold: 5,
  }),
  veteran: Object.freeze({
    reaction: 13, jitter: 7, guardChance: 0.76, punishChance: 0.65,
    throwChance: 0.23, lowChance: 0.23, mistakeChance: 0.045, actionHold: 4,
  }),
  iron: Object.freeze({
    reaction: 8, jitter: 4, guardChance: 0.87, punishChance: 0.8,
    throwChance: 0.27, lowChance: 0.25, mistakeChance: 0.015, actionHold: 3,
  }),
});

/**
 * Pure tactical policy, exported for unit tests.
 * Returns an intent label; input translation is owned by AiSystem.
 */
export function selectTacticalIntent(o, roll, profile = AI_DIFFICULTIES.skilled) {
  if (!o.actionable) return o.down ? 'wakeup' : 'wait';
  if (roll < profile.mistakeChance) return o.gap > 1.2 ? 'hesitate' : 'retreat';
  if (o.threatening && o.gap <= o.threatRange + 0.18) {
    if (roll < profile.guardChance) return o.threatLevel === 'low' ? 'guard-low' : 'guard';
    if (o.playerRecovering && roll < profile.punishChance) return 'punish';
  }
  if (o.playerRecovering && o.gap < 1.28 && roll < profile.punishChance) return 'punish';
  if (o.gap > 1.68) return 'approach';
  if (o.gap > 1.18) return roll < 0.52 ? 'long-poke' : 'approach';
  if (o.finisherReady && o.gap < 1.32) return 'finisher';
  if (o.gap < 0.82 && roll < profile.throwChance + 0.16) return 'throw';
  if (roll < profile.lowChance + 0.22) return 'low';
  if (roll > 0.82) return 'heavy';
  return 'jab';
}

export class AiSystem extends System {
  static id = 'ai';
  static deps = ['combat-sim'];
  static budgetMs = 0.25;

  constructor() {
    super();
    this.player = 1;
    this.input = null;
    this.profile = AI_DIFFICULTIES.skilled;
    this.thinkTicks = 0;
    this.actionTicks = 0;
    this.intent = 'wait';
    this.raw = makeRaw();
    this.observation = {
      actionable: false,
      down: false,
      gap: 0,
      threatening: false,
      threatRange: 0,
      threatLevel: 'mid',
      playerRecovering: false,
      finisherReady: false,
    };
    this.info = {
      tick: 0,
      goal: 'observe',
      selectedAction: 'wait',
      evaluatedOptions: 'approach / guard / strike / throw',
      estimatedRisk: 0,
      reactionDelay: 0,
      recognizedTendency: 'sampling',
      distance: 0,
      difficulty: 'skilled',
    };
    this._lastPlayerMove = '';
    this._repeatedMove = 0;
    this._roundOff = null;
  }

  async init(ctx) {
    this.input = ctx.systems.get('core-input');
    if (!this.input) throw new Error('ai: InputSystem is unavailable.');
    this.profile = AI_DIFFICULTIES[ctx.app.aiDifficulty]
      || AI_DIFFICULTIES[ctx.config.get('aiDifficulty')]
      || AI_DIFFICULTIES.skilled;
    this.input.setSyntheticInput(this.player, this.raw);
    ctx.input.assignments[this.player] = 'computer-ai';
    this._roundOff = ctx.events.on('combat:round-start', () => this.reset(ctx));
    ctx.log.info(`ai: Player 2 computer enabled (${ctx.app.aiDifficulty || 'skilled'})`);
  }

  fixedUpdate(step, ctx) {
    const active = ctx.app.mode === 'vs-ai'
      && !ctx.flags.capture
      && !ctx.flags.trainingMode
      && ctx.sim.match
      && ctx.sim.match.phase === PHASE.FIGHT
      && ctx.sim.match.timerEnabled;

    if (!active) {
      clearRaw(this.raw);
      this.input.setSyntheticInput(this.player, this.raw);
      this.intent = 'wait';
      return;
    }

    const selectedDifficulty = ctx.app.aiDifficulty || ctx.config.get('aiDifficulty');
    this.profile = AI_DIFFICULTIES[selectedDifficulty] || AI_DIFFICULTIES.skilled;

    if (this.actionTicks > 0) {
      this.actionTicks--;
      if (this.actionTicks === 0) clearRaw(this.raw);
      this.input.setSyntheticInput(this.player, this.raw);
      this._updateTelemetry(ctx);
      return;
    }

    clearRaw(this.raw);
    if (this.thinkTicks > 0) {
      this.thinkTicks--;
      this.input.setSyntheticInput(this.player, this.raw);
      this._updateTelemetry(ctx);
      return;
    }

    this._observe(ctx);
    const roll = ctx.rng.ai.next();
    this.intent = selectTacticalIntent(this.observation, roll, this.profile);
    this._translateIntent(ctx);
    this.actionTicks = actionDuration(this.intent, this.profile, ctx.rng.ai);
    this.thinkTicks = this.profile.reaction + ctx.rng.ai.int(0, this.profile.jitter + 1);
    this.input.setSyntheticInput(this.player, this.raw);
    this._updateTelemetry(ctx);
    void step;
  }

  _observe(ctx) {
    const human = ctx.sim.fighters[0];
    const cpu = ctx.sim.fighters[this.player];
    const move = human.move;
    // Move aiHints ranges are authored from fighter origin to fighter origin, matching move
    // hitbox reach. Comparing them with edge-to-edge pushbox gap made the CPU stop roughly
    // 0.68 m too early and whiff forever.
    const gap = Math.abs(cpu.x - human.x);
    const o = this.observation;
    o.actionable = cpu.actionLockTicks <= 0 && !cpu.inHitstun && !cpu.down
      && cpu.throwPartner < 0 && !cpu.move;
    o.down = cpu.down;
    o.gap = gap;
    o.threatening = !!move && (human.state === 'attack-startup' || human.state === 'attack-active');
    o.threatRange = move && move.aiHints ? move.aiHints.range : 0.85;
    o.threatLevel = move ? move.level : 'mid';
    o.playerRecovering = !!move && human.state === 'attack-recovery';
    o.finisherReady = cpu.meter >= cpu.meterMax * 0.78 && human.health <= human.maxHealth * 0.24;

    if (human.moveId && human.moveId === this._lastPlayerMove) this._repeatedMove++;
    else if (human.moveId) {
      this._lastPlayerMove = human.moveId;
      this._repeatedMove = 1;
    }
  }

  _translateIntent(ctx) {
    const cpu = ctx.sim.fighters[this.player];
    const human = ctx.sim.fighters[0];
    switch (this.intent) {
      case 'approach':
        setForward(this.raw, cpu.facing);
        break;
      case 'retreat':
        setBack(this.raw, cpu.facing);
        this.raw.guard = true;
        break;
      case 'guard':
        this.raw.guard = true;
        break;
      case 'guard-low':
        this.raw.guard = true;
        this.raw.down = true;
        break;
      case 'long-poke':
        this.raw.hp = true;
        break;
      case 'punish':
        if (this.observation.gap < 0.92) {
          setForward(this.raw, cpu.facing);
          this.raw.hk = true;
        } else {
          this.raw.hp = true;
        }
        break;
      case 'throw':
        this.raw.lp = true;
        this.raw.lk = true;
        break;
      case 'low':
        this.raw.down = true;
        this.raw.lk = true;
        break;
      case 'heavy':
        if (this.observation.gap < 1.02) setForward(this.raw, cpu.facing);
        this.raw.hp = true;
        break;
      case 'finisher':
        this.raw.hp = true;
        this.raw.hk = true;
        break;
      case 'wakeup':
        this.raw.lk = true;
        break;
      case 'jab':
        this.raw.lp = true;
        break;
      default:
        break;
    }
    void human;
  }

  _updateTelemetry(ctx) {
    const human = ctx.sim.fighters[0];
    const cpu = ctx.sim.fighters[this.player];
    const gap = Math.abs(cpu.x - human.x);
    const t = this.info;
    t.tick = ctx.time.tick;
    t.goal = this.intent === 'approach' ? 'close distance'
      : this.intent.startsWith('guard') ? 'defend visible threat'
      : this.intent === 'retreat' ? 'reset spacing'
      : this.intent === 'wait' || this.intent === 'hesitate' ? 'observe'
      : 'create offense';
    t.selectedAction = this.intent;
    t.estimatedRisk = +clamp(
      (this.intent === 'heavy' || this.intent === 'finisher' ? 0.72 : 0.28)
      + (gap > 1.3 ? 0.16 : 0)
      - (human.state === 'attack-recovery' ? 0.22 : 0),
      0.05,
      0.95,
    ).toFixed(2);
    t.reactionDelay = this.profile.reaction;
    t.recognizedTendency = this._repeatedMove >= 3
      ? `repeat: ${this._lastPlayerMove}` : 'varied offense';
    t.distance = +gap.toFixed(3);
    t.difficulty = ctx.app.aiDifficulty || 'skilled';
  }

  telemetry() {
    return { ...this.info };
  }

  reset(ctx) {
    this.thinkTicks = this.profile.reaction;
    this.actionTicks = 0;
    this.intent = 'wait';
    this._lastPlayerMove = '';
    this._repeatedMove = 0;
    clearRaw(this.raw);
    if (this.input) this.input.setSyntheticInput(this.player, this.raw);
    void ctx;
  }

  dispose() {
    if (this._roundOff) this._roundOff();
    if (this.input) this.input.clearSynthetic(this.player);
    this.input = null;
  }
}

function actionDuration(intent, profile, rng) {
  if (intent === 'approach') return profile.actionHold + rng.int(8, 19);
  if (intent === 'retreat') return profile.actionHold + rng.int(5, 12);
  if (intent === 'guard' || intent === 'guard-low') return profile.actionHold + rng.int(5, 14);
  if (intent === 'hesitate' || intent === 'wait') return profile.actionHold + rng.int(4, 10);
  return 3;
}

function makeRaw() {
  return {
    up: false, down: false, left: false, right: false,
    lp: false, hp: false, lk: false, hk: false, sp: false, guard: false,
  };
}

function clearRaw(raw) {
  raw.up = false;
  raw.down = false;
  raw.left = false;
  raw.right = false;
  raw.lp = false;
  raw.hp = false;
  raw.lk = false;
  raw.hk = false;
  raw.sp = false;
  raw.guard = false;
}

function setForward(raw, facing) {
  if (facing > 0) raw.right = true;
  else raw.left = true;
}

function setBack(raw, facing) {
  if (facing > 0) raw.left = true;
  else raw.right = true;
}
